import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Calendar } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";
import { stripLangPrefix } from "@/lib/i18n";

export default function SiteHeader() {
  const { t, lang, setLang, pathWithLang, supported } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const NAV = [
    { to: "/", label: t("nav.home") },
    { to: "/dienste", label: t("nav.services") },
    { to: "/erfahrung", label: t("nav.experience") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/ueber-mich", label: t("nav.about") },
    { to: "/kontakt", label: t("nav.contact") },
  ];

  const isActive = (to) => {
    const current = stripLangPrefix(pathname) || "/";
    if (to === "/") return current === "/";
    return current === to || current.startsWith(to + "/");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[var(--bg-0)]/85 backdrop-blur-xl border-b hairline" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-[80px] flex items-center justify-between gap-4">
        <Link
          to={pathWithLang("/")}
          data-testid="logo-link"
          aria-label="Karim Benna"
          className="shrink-0 flex items-baseline gap-0.5 group"
        >
          <span className="font-display text-lg lg:text-xl tracking-tight text-[var(--text-0)] group-hover:text-[var(--gold)] transition-colors">
            Karim Benna
          </span>
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] ml-1 self-end mb-1.5" aria-hidden="true" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={pathWithLang(item.to)}
              data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
              className={`link-gold text-sm font-medium ${isActive(item.to) ? "text-[var(--gold)]" : ""}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div
            data-testid="lang-switcher"
            role="group"
            aria-label={t("common.languageLabel")}
            className="hidden md:flex items-center gap-1 mr-1"
          >
            {supported.map((l, i) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}`}
                aria-pressed={lang === l}
                className={`px-2 py-1 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                  lang === l ? "text-[var(--gold)] font-semibold" : "text-[var(--text-2)] hover:text-[var(--text-0)]"
                }`}
              >
                {l}
                {i < supported.length - 1 && (
                  <span className="text-[var(--text-3)] ml-1 select-none">·</span>
                )}
              </button>
            ))}
          </div>

          <Link
            to={pathWithLang("/kontakt")}
            data-testid="header-book-btn"
            className="hidden md:inline-flex btn-primary text-xs"
          >
            <Calendar size={14} />
            {t("common.bookCall")}
          </Link>
          <button
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[var(--text-0)] p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-nav" className="lg:hidden bg-[var(--bg-0)]/95 backdrop-blur-2xl border-t hairline">
          <div className="px-6 py-8 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={pathWithLang(item.to)}
                data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                className={`text-lg font-medium ${isActive(item.to) ? "text-[var(--gold)]" : "text-[var(--text-0)]"}`}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 mt-2">
              {supported.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded-full transition-colors ${
                    lang === l ? "border-[var(--gold)] text-[var(--gold)]" : "hairline text-[var(--text-2)]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <Link to={pathWithLang("/kontakt")} data-testid="mobile-book-btn" className="btn-primary text-xs mt-2 w-fit">
              <Calendar size={14} /> {t("common.bookCall")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
